import React, { useEffect, useState } from "react";

import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import {
  LuAlertCircle,
  LuBuilding2,
  LuFileText,
  LuHeading,
  LuMapPin,
  LuQuote,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getReportInfo,
  setReportInfo,
} from "../../features/appSettings/appSettingsSlice";
import { PUT } from "../../services/axios.service";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import { ReportTitleType } from "../../types/settings";
import ImageCropUpload, { AspectOption, OutputBox } from "../imageCropUpload";

interface IFormInput {
  companyName: string;
  address1: string;
  address2: string;
  tagline: string;
  titleType: ReportTitleType;
}

const titleTypeOptions: { value: ReportTitleType; label: string }[] = [
  { value: "name", label: "કંપની નામ" },
  { value: "logo", label: "લોગો" },
];

const logoAspectOptions: AspectOption[] = [
  { label: "4:1", value: 4 },
  { label: "3:1", value: 3 },
  { label: "2:1", value: 2 },
  { label: "1:1", value: 1 },
];

const logoOutputBox: OutputBox = { width: 1200, height: 300 };

const watermarkAspectOptions: AspectOption[] = [
  { label: "1:1", value: 1 },
  { label: "2:1", value: 2 },
  { label: "3:1", value: 3 },
  { label: "4:1", value: 4 },
];

const watermarkOutputBox: OutputBox = { width: 800, height: 800 };

const inputClass = (hasError: boolean) =>
  `flex w-full bg-white px-3 py-2 ring-offset-white placeholder:text-[hsl(215_16%_47%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 pr-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
  }`;

const ReportInfoForm = () => {
  const dispatch = useAppDispatch();
  const reportInfo = useAppSelector(getReportInfo);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [logo, setLogo] = useState<string | null>(null);
  const [isLogoRemoved, setIsLogoRemoved] = useState<boolean>(false);
  const [logoError, setLogoError] = useState<string>("");
  const [watermark, setWatermark] = useState<string | null>(null);
  const [isWatermarkRemoved, setIsWatermarkRemoved] = useState<boolean>(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<IFormInput>({
    mode: "onSubmit",
    defaultValues: {
      companyName: "",
      address1: "",
      address2: "",
      tagline: "",
      titleType: "name",
    },
  });

  const watchTitleType = watch("titleType");
  const savedLogo = isLogoRemoved ? null : reportInfo.logo;
  const savedWatermark = isWatermarkRemoved ? null : reportInfo.watermark;

  useEffect(() => {
    reset({
      companyName: reportInfo.companyName,
      address1: reportInfo.address1,
      address2: reportInfo.address2,
      tagline: reportInfo.tagline,
      titleType: reportInfo.titleType,
    });
  }, [reportInfo, reset]);

  const handleLogoChange = (dataUrl: string | null) => {
    setLogo(dataUrl);
    setIsLogoRemoved(dataUrl === null);
    setLogoError("");
  };

  const handleWatermarkChange = (dataUrl: string | null) => {
    setWatermark(dataUrl);
    setIsWatermarkRemoved(dataUrl === null);
  };

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (data.titleType === "logo" && !logo && !savedLogo) {
      setLogoError("PDF ટાઈટલ માટે લોગો અપલોડ કરો.");
      return;
    }

    setIsLoading(true);

    try {
      await PUT("/v1/admin/settings/report-info", {
        companyName: (data.companyName || "").trim(),
        address1: (data.address1 || "").trim(),
        address2: (data.address2 || "").trim(),
        tagline: (data.tagline || "").trim(),
        titleType: data.titleType,
        ...(logo ? { logo } : isLogoRemoved ? { logo: null } : {}),
        ...(watermark
          ? { watermark }
          : isWatermarkRemoved
          ? { watermark: null }
          : {}),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            setLogo(null);
            setIsLogoRemoved(false);
            setWatermark(null);
            setIsWatermarkRemoved(false);
            dispatch(setReportInfo(response.data.data));
            toast.success(response.data.message, { ...(toastStyles as any) });
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <div className="flex flex-col space-y-1.5 p-6">
        <div className="text-2xl font-bold text-[#113F67] leading-none tracking-tight flex items-center gap-2">
          <LuFileText className="h-6 w-6 text-[#34699A]" />
          PDF રિપોર્ટ માહિતી
        </div>
        <div className="text-sm text-gray-600">
          PDF રિપોર્ટના હેડર અને ફૂટરમાં છપાતી કંપનીની માહિતી. ટાઈટલમાં કંપની
          નામ અથવા લોગો બતાવી શકાય છે.
        </div>
      </div>

      <div className="p-6 pt-0">
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                htmlFor="reportInfo-companyName"
                className="text-sm font-semibold text-[#113F67]"
              >
                કંપની નામ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuBuilding2 className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="reportInfo-companyName"
                  type="text"
                  maxLength={100}
                  placeholder="કંપની નામ દાખલ કરો"
                  {...register("companyName", {
                    validate: (value) => (value || "").trim() !== "",
                  })}
                  className={inputClass(!!errors.companyName)}
                />
                {errors.companyName && (
                  <LuAlertCircle className="absolute right-4 top-4 h-5 w-5 text-red-500" />
                )}
              </div>

              {errors.companyName ? (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  કંપની નામ દાખલ કરો
                </p>
              ) : (
                <p className="text-gray-500 text-sm mt-1">
                  આ નામ દરેક PDF ના ફૂટરમાં પણ છપાશે.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#113F67]">
                PDF ટાઈટલ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuHeading className="absolute left-4 z-[1] top-3.5 h-5 w-5 text-[#34699A]" />
                <Controller
                  name="titleType"
                  control={control}
                  render={({ field }) => (
                    <ReactSelect
                      value={
                        titleTypeOptions.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      options={titleTypeOptions}
                      placeholder="પસંદ કરો"
                      isSearchable={false}
                      styles={iconSelectStyles as any}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      onChange={(option: any) => {
                        field.onChange(option?.value);
                        setLogoError("");
                      }}
                    />
                  )}
                />
              </div>

              <p className="text-gray-500 text-sm mt-1">
                લોગો પસંદ કરશો તો PDF ના ટાઈટલમાં કંપની નામની જગ્યાએ લોગો છપાશે.
              </p>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="reportInfo-address1"
                className="text-sm font-semibold text-[#113F67]"
              >
                સરનામું લાઈન 1
              </label>

              <div className="relative">
                <LuMapPin className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="reportInfo-address1"
                  type="text"
                  maxLength={150}
                  placeholder="સરનામું લાઈન 1 દાખલ કરો"
                  {...register("address1")}
                  className={inputClass(false)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="reportInfo-address2"
                className="text-sm font-semibold text-[#113F67]"
              >
                સરનામું લાઈન 2
              </label>

              <div className="relative">
                <LuMapPin className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="reportInfo-address2"
                  type="text"
                  maxLength={150}
                  placeholder="સરનામું લાઈન 2 દાખલ કરો"
                  {...register("address2")}
                  className={inputClass(false)}
                />
              </div>
            </div>

            <div className="space-y-2 md:col-span-2">
              <label
                htmlFor="reportInfo-tagline"
                className="text-sm font-semibold text-[#113F67]"
              >
                ટેગલાઈન (વૈકલ્પિક)
              </label>

              <div className="relative">
                <LuQuote className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="reportInfo-tagline"
                  type="text"
                  maxLength={150}
                  placeholder="ટેગલાઈન દાખલ કરો"
                  {...register("tagline")}
                  className={inputClass(false)}
                />
              </div>

              <p className="text-gray-500 text-sm mt-1">
                ઉમેરશો તો દરેક PDF રિપોર્ટની સૌથી ઉપરની લાઈનમાં વચ્ચે નાના બોલ્ડ અક્ષરે છપાશે.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#113F67]">
                લોગો
                {watchTitleType === "logo" && (
                  <span className="text-red-500"> *</span>
                )}
              </label>

              <ImageCropUpload
                label="લોગો"
                value={logo}
                existingImageUrl={savedLogo}
                onChange={handleLogoChange}
                aspectOptions={logoAspectOptions}
                outputBox={logoOutputBox}
                hasError={!!logoError}
              />

              {logoError ? (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {logoError}
                </p>
              ) : (
                <p className="text-gray-500 text-sm mt-1">
                  ક્રોપ કરેલો લોગો PDF ના ટાઈટલમાં 60px ઊંચાઈએ મધ્યમાં છપાશે.
                  પારદર્શક બેકગ્રાઉન્ડ માટે PNG વાપરો.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#113F67]">
                વોટરમાર્ક (વૈકલ્પિક)
              </label>

              <ImageCropUpload
                label="વોટરમાર્ક"
                value={watermark}
                existingImageUrl={savedWatermark}
                onChange={handleWatermarkChange}
                aspectOptions={watermarkAspectOptions}
                outputBox={watermarkOutputBox}
              />

              <p className="text-gray-500 text-sm mt-1">
                ઉમેરશો તો દરેક PDF રિપોર્ટના દરેક પેજની વચ્ચે આછો છપાશે.
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none h-10 px-6 bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-white font-semibold text-base rounded-lg transition-all duration-300 transform hover:scale-[1.01] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  સેવ થઈ રહ્યું છે...
                </div>
              ) : (
                "સાચવો"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default ReportInfoForm;
